/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { authenticatedFetch } from "@lumi/data";
import { AlertTriangle, CheckCircle2, Clock, RefreshCw, XCircle } from "lucide-react";
import { useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useAuth } from "../../../../auth/AuthContext";
import { createRefreshCallback } from "../../../../auth/refreshCallback";
import { SubtitleManagementDialog } from "../../../../shared/components/subtitle";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { TableCell, TableRow } from "../../../../shared/components/ui/table";
import type { BulkEditTitle, VideoAsset } from "../types";
import { formatDuration, getStatusBadge } from "../utils/videoAssetHelpers";
import { EditVideoAssetModal } from "./EditVideoAssetModal";
import { TitleActionsDropdown } from "./TitleActionsDropdown";

interface VideoAssetsRowProps {
  readonly title: BulkEditTitle;
  readonly isSelected: boolean;
  readonly onSelect: (titleId: string, selected: boolean) => void;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
  readonly onRefresh?: (titleId: string) => Promise<void>;
}

/**
 * Render FFprobe validation status icon with tooltip (like VisualAssetsRow pattern)
 * Shows green checkmark if valid, yellow warning if warnings exist, red X if errors
 */
function ValidationStatusIcon({ asset }: { readonly asset: VideoAsset }): ReactElement | null {
  const ffprobe = asset.ffprobe;
  if (!ffprobe?.validation) return null;

  const { valid, errors, warnings } = ffprobe.validation;
  const hasWarnings = warnings.length > 0;
  const hasErrors = errors.length > 0;
  const messages = [...errors, ...warnings];

  // No issues - show green check (no tooltip needed)
  if (valid && !hasWarnings && !hasErrors) {
    return <CheckCircle2 className="h-3 w-3 text-green-400 ml-1" />;
  }

  // Has errors - show red X with tooltip
  if (!valid || hasErrors) {
    return (
      <span className="relative group ml-1">
        <XCircle className="h-3 w-3 text-red-400" />
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-all duration-100 delay-300 z-50 pointer-events-none">
          <div className="bg-neutral-900 border border-red-700/30 rounded px-3 py-2 text-xs text-red-300 whitespace-nowrap shadow-lg">
            {messages.slice(0, 3).map((msg) => (
              <div key={`msg-${msg.slice(0, 20)}`} className="flex items-start gap-1">
                <span>•</span>
                <span>{msg}</span>
              </div>
            ))}
            {messages.length > 3 && <div className="text-neutral-500 mt-1">+{messages.length - 3} more...</div>}
          </div>
        </div>
      </span>
    );
  }

  // Has warnings only - show yellow checkmark with tooltip (matches VisualAssetsRow pattern)
  return (
    <span className="relative group ml-1">
      <CheckCircle2 className="h-3 w-3 text-yellow-400" />
      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-all duration-100 delay-300 z-50 pointer-events-none">
        <div className="bg-neutral-900 border border-yellow-700/30 rounded px-3 py-2 text-xs text-yellow-300 whitespace-nowrap shadow-lg flex items-start gap-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
          <span>{warnings.join("; ")}</span>
        </div>
      </div>
    </span>
  );
}

/**
 * Single row in the Video Assets view table
 * Shows: Title, Feature video + subs, Trailer video + subs, Status
 */
export function VideoAssetsRow({
  title,
  isSelected,
  onSelect,
  onDelete,
  onDuplicate,
  onRefresh,
}: Readonly<VideoAssetsRowProps>): ReactElement {
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [showFeatureSubsModal, setShowFeatureSubsModal] = useState(false);
  const [showTrailerSubsModal, setShowTrailerSubsModal] = useState(false);
  const [regeneratingFeatureThumbnails, setRegeneratingFeatureThumbnails] = useState(false);
  const [regeneratingTrailerThumbnails, setRegeneratingTrailerThumbnails] = useState(false);

  const { accessToken, refreshAuth } = useAuth();
  const featureAsset = title.assets?.find((a) => a.kind === "FEATURE");
  const trailerAsset = title.assets?.find((a) => a.kind === "TRAILER");

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  const handleRegenerateThumbnails = async (assetId: string, kind: "FEATURE" | "TRAILER") => {
    if (!accessToken) {
      toast.error("Not authenticated");
      return;
    }

    const setLoading = kind === "FEATURE" ? setRegeneratingFeatureThumbnails : setRegeneratingTrailerThumbnails;

    setLoading(true);
    try {
      const result = await authenticatedFetch<{ vttUrl: string }>(
        `${API_BASE}/v1/admin/assets/${assetId}/regenerate-thumbnails`,
        {
          method: "POST",
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );
      toast.success(`Thumbnails regenerated successfully for ${kind.toLowerCase()} video`);

      // Optimistically update the UI
      if (kind === "FEATURE" && featureAsset) {
        featureAsset.thumbnailVttUrl = result.vttUrl;
      } else if (kind === "TRAILER" && trailerAsset) {
        trailerAsset.thumbnailVttUrl = result.vttUrl;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to regenerate thumbnails: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRevalidate = async (assetId: string): Promise<void> => {
    if (!accessToken) {
      toast.error("Not authenticated");
      return;
    }

    // Track which modal is currently open
    const isFeatureModalOpen = showFeatureModal;
    const isTrailerModalOpen = showTrailerModal;

    try {
      await authenticatedFetch(
        `${API_BASE}/v1/admin/assets/${assetId}/validate-video`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ force: true, updateDatabase: true }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );
      toast.success("Video analysis complete");

      // Refresh title data to get updated FFprobe metadata
      if (onRefresh) {
        // Close modal first
        if (isFeatureModalOpen) setShowFeatureModal(false);
        if (isTrailerModalOpen) setShowTrailerModal(false);

        // Fetch updated data
        await onRefresh(title.id);

        // Wait for React to process state update, then reopen modal
        // Using requestAnimationFrame + setTimeout ensures state has propagated
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (isFeatureModalOpen) setShowFeatureModal(true);
            if (isTrailerModalOpen) setShowTrailerModal(true);
          }, 100);
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Video analysis failed: ${message}`);
    }
  };

  // Feature video display
  const featureStatusBadge = getStatusBadge(featureAsset?.encodingStatus ?? null);
  const featureVideoDisplay = featureAsset ? (
    <div className="flex items-center gap-1.5">
      <span className={featureStatusBadge.color}>{featureStatusBadge.icon}</span>
      <span className="text-xs text-neutral-300">{featureStatusBadge.label}</span>
      {featureAsset.durationSec && (
        <span className="text-xs text-neutral-500">• {formatDuration(featureAsset.durationSec)}</span>
      )}
      {/* FFprobe validation status (shows checkmark, warning, or error icon) */}
      <ValidationStatusIcon asset={featureAsset} />
    </div>
  ) : (
    <span className="text-xs text-neutral-600 italic">No feature video</span>
  );

  // Feature subtitles display
  const featureSubtitles = featureAsset?.subtitles || [];
  const featureSubsDisplay = (() => {
    if (featureSubtitles.length === 0) {
      return <span className="text-xs text-neutral-600 italic">No subs</span>;
    }
    const langs = featureSubtitles.slice(0, 3).map((s) => s.lang.toUpperCase());
    const remaining = featureSubtitles.length - 3;
    return (
      <span className="text-xs text-neutral-300">
        {langs.join(", ")}
        {remaining > 0 && <span className="text-neutral-500"> +{remaining} more</span>}
      </span>
    );
  })();

  // Trailer video display
  const trailerStatusBadge = getStatusBadge(trailerAsset?.encodingStatus ?? null);
  const trailerVideoDisplay = trailerAsset ? (
    <div className="flex items-center gap-1.5">
      <span className={trailerStatusBadge.color}>{trailerStatusBadge.icon}</span>
      <span className="text-xs text-neutral-300">{trailerStatusBadge.label}</span>
      {trailerAsset.durationSec && (
        <span className="text-xs text-neutral-500">• {formatDuration(trailerAsset.durationSec)}</span>
      )}
      {/* FFprobe validation status (shows checkmark, warning, or error icon) */}
      <ValidationStatusIcon asset={trailerAsset} />
    </div>
  ) : (
    <div className="flex items-center gap-1.5">
      <XCircle className="h-3 w-3 text-neutral-600" />
      <span className="text-xs text-neutral-600 italic">Missing</span>
    </div>
  );

  // Trailer subtitles display
  const trailerSubtitles = trailerAsset?.subtitles || [];
  const trailerSubsDisplay = (() => {
    if (trailerSubtitles.length === 0) {
      return <span className="text-xs text-neutral-600 italic">No subs</span>;
    }
    const langs = trailerSubtitles.slice(0, 3).map((s) => s.lang.toUpperCase());
    const remaining = trailerSubtitles.length - 3;
    return (
      <span className="text-xs text-neutral-300">
        {langs.join(", ")}
        {remaining > 0 && <span className="text-neutral-500"> +{remaining} more</span>}
      </span>
    );
  })();

  // Overall status (complete if both feature and trailer are ready)
  const hasFeature = featureAsset?.encodingStatus === "READY";
  const hasTrailer = trailerAsset?.encodingStatus === "READY";
  const hasFeatureSubs = featureSubtitles.length > 0;
  const isComplete = hasFeature && hasFeatureSubs;
  const isPartial = hasFeature || hasTrailer;

  const handleFeatureSave = () => {
    // Subtitle management will be implemented later
    setShowFeatureModal(false);
  };

  const handleTrailerSave = () => {
    // Subtitle management will be implemented later
    setShowTrailerModal(false);
  };

  return (
    <TableRow className="hover:bg-neutral-900/30">
      <TableCell className="w-12">
        <Checkbox checked={isSelected} onCheckedChange={(checked) => onSelect(title.id, checked === true)} />
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-white truncate">{title.name}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFeatureModal(true)}
            className="flex flex-col gap-1 text-left hover:opacity-80 transition-opacity group flex-1"
            disabled={!featureAsset}
          >
            {featureVideoDisplay}
          </button>
          {featureAsset?.encodingStatus === "READY" && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void handleRegenerateThumbnails(featureAsset.id, "FEATURE");
              }}
              disabled={regeneratingFeatureThumbnails}
              className="p-1 hover:bg-neutral-700 rounded transition-colors disabled:opacity-50 flex-shrink-0"
              title="Regenerate thumbnails"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 text-neutral-400 ${regeneratingFeatureThumbnails ? "animate-spin" : ""}`}
              />
            </button>
          )}
        </div>
      </TableCell>
      <TableCell>
        <button
          type="button"
          onClick={() => setShowFeatureSubsModal(true)}
          className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity group w-full"
          disabled={!featureAsset}
        >
          {featureSubsDisplay}
        </button>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowTrailerModal(true)}
            className="flex flex-col gap-1 text-left hover:opacity-80 transition-opacity group flex-1"
            disabled={!trailerAsset}
          >
            {trailerVideoDisplay}
          </button>
          {trailerAsset?.encodingStatus === "READY" && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void handleRegenerateThumbnails(trailerAsset.id, "TRAILER");
              }}
              disabled={regeneratingTrailerThumbnails}
              className="p-1 hover:bg-neutral-700 rounded transition-colors disabled:opacity-50 flex-shrink-0"
              title="Regenerate thumbnails"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 text-neutral-400 ${regeneratingTrailerThumbnails ? "animate-spin" : ""}`}
              />
            </button>
          )}
        </div>
      </TableCell>
      <TableCell>
        <button
          type="button"
          onClick={() => setShowTrailerSubsModal(true)}
          className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity group w-full"
          disabled={!trailerAsset}
        >
          {trailerSubsDisplay}
        </button>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          {isComplete && <CheckCircle2 className="h-4 w-4 text-green-400" />}
          {isPartial && !isComplete && <Clock className="h-4 w-4 text-yellow-400" />}
          {!isPartial && <XCircle className="h-4 w-4 text-red-400" />}
          <span className="text-xs text-neutral-400">
            {isComplete && "Complete"}
            {isPartial && !isComplete && "Partial"}
            {!isPartial && "Missing"}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-center">
        <TitleActionsDropdown titleId={title.id} titleName={title.name} onDelete={onDelete} onDuplicate={onDuplicate} />
      </TableCell>

      {/* Edit Feature Modal */}
      {featureAsset &&
        createPortal(
          <EditVideoAssetModal
            open={showFeatureModal}
            onClose={() => setShowFeatureModal(false)}
            titleName={title.name}
            asset={featureAsset}
            onSave={handleFeatureSave}
            onRevalidate={handleRevalidate}
          />,
          document.body,
        )}

      {/* Edit Trailer Modal */}
      {trailerAsset &&
        createPortal(
          <EditVideoAssetModal
            open={showTrailerModal}
            onClose={() => setShowTrailerModal(false)}
            titleName={title.name}
            asset={trailerAsset}
            onSave={handleTrailerSave}
            onRevalidate={handleRevalidate}
          />,
          document.body,
        )}

      {/* Feature Subtitles Modal */}
      {featureAsset &&
        createPortal(
          <SubtitleManagementDialog
            open={showFeatureSubsModal}
            onClose={() => setShowFeatureSubsModal(false)}
            titleName={title.name}
            assetKind="feature"
            assetId={featureAsset.id}
            subtitles={featureAsset.subtitles}
            onRefresh={onRefresh ? () => onRefresh(title.id) : async () => {}}
          />,
          document.body,
        )}

      {/* Trailer Subtitles Modal */}
      {trailerAsset &&
        createPortal(
          <SubtitleManagementDialog
            open={showTrailerSubsModal}
            onClose={() => setShowTrailerSubsModal(false)}
            titleName={title.name}
            assetKind="trailer"
            assetId={trailerAsset.id}
            subtitles={trailerAsset.subtitles}
            onRefresh={onRefresh ? () => onRefresh(title.id) : async () => {}}
          />,
          document.body,
        )}
    </TableRow>
  );
}
